import { useEffect, useMemo, useState } from "react";
import { Calendar, CheckCircle2, ChevronDown, ChevronRight, Clock, MapPin, Shield, Users, Zap } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useEffectiveInstructorId,
  useGroupsForInstructor,
  useElementsForStandard,
  useSessionsByGroup,
} from "@/store/hooks";
import type { GroupSession } from "@/data/index";

const today = new Date().toISOString().slice(0, 10);

function statusOf(fecha: string): "pasado" | "hoy" | "futuro" {
  if (fecha < today) return "pasado";
  if (fecha === today) return "hoy";
  return "futuro";
}

const statusTone: Record<string, "gray" | "teal" | "navy"> = {
  pasado: "gray",
  hoy: "teal",
  futuro: "navy",
};

const statusLabel: Record<string, string> = {
  pasado: "Realizada",
  hoy: "Hoy",
  futuro: "Próxima",
};

function CriterioChip({ id, allCriterios }: { id: string; allCriterios: Record<string, { codigo: string; descripcion: string; tipo: string }> }) {
  const c = allCriterios[id];
  if (!c) return null;
  const tone =
    c.tipo === "desempeño" ? "navy"
    : c.tipo === "producto" ? "teal"
    : c.tipo === "actitud" ? "amber"
    : "gray";
  return (
    <span
      title={c.descripcion}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: tone === "navy" ? "var(--cc-navy-bg, #eff6ff)" : tone === "teal" ? "var(--cc-teal-light)" : tone === "amber" ? "var(--cc-amber-bg)" : "#f3f4f6",
        color: tone === "navy" ? "var(--cc-navy)" : tone === "teal" ? "var(--cc-teal)" : tone === "amber" ? "var(--cc-amber)" : "var(--cc-gray-600)",
        border: "1px solid",
        borderColor: tone === "navy" ? "#bfdbfe" : tone === "teal" ? "var(--cc-teal)" : tone === "amber" ? "var(--cc-amber)" : "var(--cc-gray-200)",
        cursor: "help",
        whiteSpace: "nowrap",
      }}
    >
      {c.codigo.toUpperCase()}
    </span>
  );
}

function PracticeCard({ session, allCriterios }: { session: GroupSession; allCriterios: Record<string, { codigo: string; descripcion: string; tipo: string }> }) {
  const [open, setOpen] = useState(false);
  const status = statusOf(session.fecha);
  const ejercicios = session.ejerciciosCubiertos ?? [];

  return (
    <div
      style={{
        border: "1px solid var(--cc-gray-200)",
        borderRadius: 10,
        overflow: "hidden",
        background: "white",
        borderLeft: status === "hoy" ? "4px solid var(--cc-teal)" : status === "pasado" ? "4px solid var(--cc-gray-300)" : "4px solid var(--cc-navy)",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
      >
        <span style={{ color: "var(--cc-gray-400)", flexShrink: 0, paddingTop: 2 }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <Badge tone={statusTone[status]}>{statusLabel[status]}</Badge>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>{session.titulo}</span>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--cc-gray-500)" }}>
              <Calendar size={12} />
              {new Date(session.fecha + "T12:00:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })} · {session.hora}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--cc-gray-500)" }}>
              <Clock size={12} />
              {session.duracionMin >= 60 ? `${Math.floor(session.duracionMin / 60)}h ${session.duracionMin % 60 > 0 ? `${session.duracionMin % 60}min` : ""}`.trim() : `${session.duracionMin} min`}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--cc-gray-500)" }}>
              <MapPin size={12} />
              {session.sede}
            </span>
          </div>
          {ejercicios.length > 0 && !open && (
            <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
              {ejercicios.slice(0, 6).map((id) => (
                <CriterioChip key={id} id={id} allCriterios={allCriterios} />
              ))}
              {ejercicios.length > 6 && (
                <span style={{ fontSize: 11, color: "var(--cc-gray-400)", alignSelf: "center" }}>+{ejercicios.length - 6} más</span>
              )}
            </div>
          )}
        </div>
      </button>

      {open && (
        <div style={{ borderTop: "1px solid var(--cc-gray-100)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
          {session.descripcion && (
            <p style={{ margin: 0, fontSize: 13, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>{session.descripcion}</p>
          )}

          {ejercicios.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 8 }}>
                Criterios EC1781 que se trabajan
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {ejercicios.map((id) => (
                  <CriterioChip key={id} id={id} allCriterios={allCriterios} />
                ))}
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
                {(["desempeño", "producto", "conocimiento", "actitud"] as const).map((tipo) => {
                  const count = ejercicios.filter((id) => allCriterios[id]?.tipo === tipo).length;
                  if (!count) return null;
                  return (
                    <span key={tipo} style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                      <strong>{count}</strong> {tipo}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {session.notas && (
            <div style={{ background: "var(--cc-gray-50)", borderRadius: 6, padding: "10px 12px", fontSize: 13, color: "var(--cc-gray-700)" }}>
              <strong>Notas:</strong> {session.notas}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function InstructorPracticas() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("instructor"), [setRole]);

  const instructorId = useEffectiveInstructorId();
  const groups = useGroupsForInstructor(instructorId);

  const sessionsByGroup = useSessionsByGroup();

  // Groups that have at least one practice session, or all groups if none have been filtered yet
  const practiceGroups = useMemo(() => {
    const withPractice = groups.filter((g) =>
      (sessionsByGroup[g.id] ?? []).some((s) => s.tipo === "practica"),
    );
    return withPractice.length > 0 ? withPractice : groups;
  }, [groups, sessionsByGroup]);

  const elements = useElementsForStandard("EC1781");

  const allCriterios = useMemo(() => {
    const map: Record<string, { codigo: string; descripcion: string; tipo: string }> = {};
    for (const el of elements) {
      for (const c of el.criterios) {
        map[c.id] = { codigo: c.codigo, descripcion: c.descripcion, tipo: c.tipo };
      }
    }
    return map;
  }, [elements]);

  const [activeGroup, setActiveGroup] = useState<string | undefined>(undefined);
  const targetGroup = activeGroup ?? practiceGroups[0]?.id;

  const practicaSessions = useMemo(() => {
    const sessions = sessionsByGroup[targetGroup ?? ""] ?? [];
    return sessions
      .filter((s) => s.tipo === "practica")
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [sessionsByGroup, targetGroup]);

  const stats = useMemo(() => {
    const realizadas = practicaSessions.filter((s) => statusOf(s.fecha) === "pasado").length;
    const proximas = practicaSessions.filter((s) => statusOf(s.fecha) === "futuro").length;
    const totalMin = practicaSessions.reduce((s, p) => s + p.duracionMin, 0);
    return { realizadas, proximas, total: practicaSessions.length, totalMin };
  }, [practicaSessions]);

  return (
    <AppLayout pageTitle="Prácticas EC1781" breadcrumb={["Instructor", "Prácticas"]}>
      {practiceGroups.length === 0 ? (
        <Card>
          <div style={{ padding: 32, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 14 }}>
            No hay grupos con prácticas registradas para este instructor.
          </div>
        </Card>
      ) : (
        <>
          {practiceGroups.length > 1 && (
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-gray-700)" }}>Grupo:</span>
                {practiceGroups.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setActiveGroup(g.id)}
                    style={{
                      padding: "4px 14px",
                      borderRadius: 20,
                      border: "1px solid",
                      borderColor: targetGroup === g.id ? "var(--cc-teal)" : "var(--cc-gray-200)",
                      background: targetGroup === g.id ? "var(--cc-teal-light)" : "white",
                      color: targetGroup === g.id ? "var(--cc-teal)" : "var(--cc-gray-600)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {g.nombre}
                  </button>
                ))}
              </div>
            </Card>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 14, alignItems: "start" }}>
            <div style={{ minWidth: 0 }}>
              <Card style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 2 }}>
                      {practiceGroups.find((g) => g.id === targetGroup)?.nombre ?? "Grupo"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
                      {practiceGroups.find((g) => g.id === targetGroup)?.sede}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 20, marginLeft: "auto" }}>
                    <StatMini label="Total prácticas" value={stats.total} />
                    <StatMini label="Realizadas" value={stats.realizadas} tone="gray" />
                    <StatMini label="Próximas" value={stats.proximas} tone="navy" />
                    <StatMini
                      label="Horas en pista"
                      value={`${Math.round(stats.totalMin / 60)}h ${stats.totalMin % 60 > 0 ? `${stats.totalMin % 60}m` : ""}`.trim()}
                      tone="teal"
                    />
                  </div>
                </div>
              </Card>

              {practicaSessions.length === 0 ? (
                <Card>
                  <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13 }}>
                    No hay sesiones de práctica registradas para este grupo.
                  </div>
                </Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {practicaSessions.map((s) => (
                    <PracticeCard key={s.id} session={s} allCriterios={allCriterios} />
                  ))}
                </div>
              )}
            </div>

            <div style={{ width: 260, flexShrink: 0 }}>
              <Card padding={0}>
                <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--cc-gray-200)", display: "flex", alignItems: "center", gap: 6 }}>
                  <Shield size={14} style={{ color: "var(--cc-teal)" }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>Criterios EC1781</span>
                </div>
                {elements.map((el) => (
                  <div key={el.id} style={{ borderBottom: "1px solid var(--cc-gray-100)" }}>
                    <div style={{ padding: "10px 14px", background: "var(--cc-gray-50)", fontSize: 11, fontWeight: 700, color: "var(--cc-gray-600)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {el.codigo} — {el.titulo.length > 40 ? el.titulo.slice(0, 40) + "…" : el.titulo}
                    </div>
                    <div style={{ padding: "8px 14px", display: "flex", flexDirection: "column", gap: 4 }}>
                      {el.criterios.map((c) => {
                        const cubierto = practicaSessions.some((s) => s.ejerciciosCubiertos?.includes(c.id));
                        return (
                          <div
                            key={c.id}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 6,
                              fontSize: 11,
                              color: cubierto ? "var(--cc-gray-700)" : "var(--cc-gray-400)",
                            }}
                          >
                            {cubierto
                              ? <CheckCircle2 size={11} style={{ color: "var(--cc-teal)", flexShrink: 0, marginTop: 1 }} />
                              : <span style={{ width: 11, height: 11, flexShrink: 0, display: "inline-block", borderRadius: "50%", border: "1.5px solid var(--cc-gray-300)", marginTop: 1 }} />
                            }
                            <span><strong>{c.codigo}</strong> {c.descripcion.length > 42 ? c.descripcion.slice(0, 42) + "…" : c.descripcion}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div style={{ padding: "10px 14px", fontSize: 11, color: "var(--cc-gray-400)" }}>
                  <Zap size={10} style={{ marginRight: 4 }} />
  Pasa el cursor sobre las etiquetas de criterio para ver la descripción completa.
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}

function StatMini({ label, value, tone }: { label: string; value: string | number; tone?: "gray" | "navy" | "teal" }) {
  const color = tone === "gray" ? "var(--cc-gray-500)" : tone === "navy" ? "var(--cc-navy)" : tone === "teal" ? "var(--cc-teal)" : "var(--cc-navy)";
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--cc-gray-400)", whiteSpace: "nowrap" }}>{label}</div>
    </div>
  );
}
